import React, { useState } from 'react'
import { Tooltip, IconButton } from '@mui/material'
import { faStickyNote, faInfoCircle} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import { AssignUser, AssignUserIcon, DnrActionIcon, FlagIcon, VerdictType } from 'components'
import { alertSnackbarContentProps } from 'components/AlertSnackbar'
import { FlagCase } from '../modals/FlagCase'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling';
import { ReviewCell } from '../../QCReview/grids/Cells'
interface FlagCellProps {
    row: any
    rowIndex: number
    testId: string
    setAlertSnackbarMainProps: (value: alertSnackbarContentProps) => void
    setAlertSnackbarMainOpen: (status: boolean) => void
}

export const FlagCell: React.FC<FlagCellProps> = ({
    row,
    rowIndex,
    testId,
    setAlertSnackbarMainProps,
    setAlertSnackbarMainOpen,
}) => {
    const { user } = useAuthedUser()
    const isReadOnly = user?.is_read_only

    const [open, setOpen] = useState(false)

    return (
        <div id={'caseMgmtAGFlagCell'}>
            <Tooltip title={row.flag?.comment ?? ''} placement="right" arrow>
                <div className={`${isReadOnly ? 'emp-disabledIcon' : ''}`}>
                    <FlagIcon
                        isFlagged={Boolean(row.flag)}
                        onClick={() => setOpen(true)}
                        className={'emp-actionIcon'}
                        rowIndex={rowIndex}
                        testId={testId}
                    />
                </div>
            </Tooltip>
            {open && (
                <FlagCase
                    row={row}
                    open={open}
                    setOpen={setOpen}
                    setAlertSnackbarMainProps={setAlertSnackbarMainProps}
                    setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                />
            )}
        </div>
    )
}

interface AssignCellProps {
    row: any
    rehydrateView: () => void
    rowIndex: number
    testId: string
    setAlertSnackbarMainProps: (value: alertSnackbarContentProps) => void
    setAlertSnackbarMainOpen: (status: boolean) => void
    selectedTabFilter: string | number
    showClientTypeIssuerUI: boolean
}

export const AssignCell: React.FC<AssignCellProps> = ({
    row,
    rehydrateView,
    rowIndex,
    testId,
    setAlertSnackbarMainProps,
    setAlertSnackbarMainOpen,
    selectedTabFilter,
    showClientTypeIssuerUI,
}) => {
    const { clientTheme } = useThemeStyling()
    const { user } = useAuthedUser()
    const isReadOnly = user?.is_read_only

    const [open, setOpen] = useState<boolean>(false)
    const toggleOpen = () => setOpen((prev) => !prev)
    const { assigned_user } = row
    const assignEnabled =
        (row.properties?.is_assign_user_enabled || showClientTypeIssuerUI) ??
        false

    return (
        <div
            className={`container`}
            id={'caseMgmtAGAssignCell'}
            style={{ fontSize: clientTheme.typography.body1.fontSize }}
        >
            {Boolean(assigned_user) ? (
                <div>
                    <Tooltip
                        title={`${row.assigned_user?.fname} ${row.assigned_user?.lname}`}
                        placement="top"
                        arrow
                    >
                        <div
                            onClick={() => {
                                if (!assignEnabled || isReadOnly) return
                                toggleOpen()
                            }}
                            className={`${'emp-assigned'} ${!assignEnabled || isReadOnly
                                ? 'emp-disabled'
                                : ''
                                }`}
                        >
                            {`${row.assigned_user?.fname} ${row.assigned_user?.lname}`}
                        </div>
                    </Tooltip>
                </div>
            ) : (
                <Tooltip
                    title={
                        assignEnabled
                            ? 'Assign'
                            : selectedTabFilter === 'new_cases'
                                ? "You don't have case assignment permission.   Ask your Administrator to grant you permission."
                                : ''
                    }
                    placement="top"
                    arrow
                >
                    <div
                        className={`${!assignEnabled || isReadOnly
                            ? 'emp-disabledIcon'
                            : ''
                            }`}
                    >
                        <AssignUserIcon
                            onClick={() => {
                                if (!assignEnabled || isReadOnly) return
                                toggleOpen()
                            }}
                            assignEnabled={assignEnabled}
                            className={'emp-actionIcon'}
                            rowIndex={rowIndex}
                            testId={testId}
                            selectedTabFilter={selectedTabFilter}
                        />
                    </div>
                </Tooltip>
            )}
            {open && (
                <AssignUser
                    selectedCaseIds={[]}
                    selectedCaseMerchantId={row.merchant_id}
                    open={open}
                    toggleOpen={toggleOpen}
                    row={row}
                    rehydrateView={rehydrateView}
                    unassign={Boolean(assigned_user)}
                    cases={[]}
                    setAlertSnackbarMainProps={setAlertSnackbarMainProps}
                    setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                />
            )}
        </div>
    )
}

interface ActionsCellProps {
    rowData: any
    rehydrateView: () => void
    testId: string
    selectedTabFilter: string
    setAlertSnackbarMainProps: (value: alertSnackbarContentProps) => void
    setAlertSnackbarMainOpen: (status: boolean) => void
    showClientTypeIssuerUI: boolean
    selectedTileType?: string
}
interface OutcomeCellProps {
    data: any
    selectedTabFilter: string | number
}

export const OutcomeCell: React.FC<OutcomeCellProps> = ({
    data,
    selectedTabFilter
}) => {

    // Determine the outcome based on the verdict, status, and cycle
    const getOutcomeText = (): string => {
        const status = data.status;
        const verdict = data.verdict;
        const cycle = data.cycle;


        // Expired = Any Cycle, Status = Expired, Verdict = Loss
        if (selectedTabFilter === 'expired' || status === 'Expired') {
            return 'Loss';
        }

        // Decline = 1st Cycle, Status = Completed, Verdict = Pending
        if (cycle?.includes('First') && status === 'Completed') {
            return 'Decline';
        }

        // Decline + No PreArb In = Status = Completed, Verdict = Pending
        // Since we can't check for PreArb In, we'll use this as a fallback for Completed status
        if (status === 'Completed') {
            return 'Pending';
        }

        // Decline + 1st Cycle + PreArb In, Status = Closed, Verdict = PreArb
        // PreArb Out + 1st Cycle, Status = Closed, Verdict = PreArb
        // Both of these cases have the same conditions we can check
        if (cycle?.includes('First') && status === 'Closed') {
            return 'Raised PreArb';
        }

        // PreArb 2nd Cycle, Status = In Progress, Verdict = Pending
        if (cycle?.includes('Second') && status === 'In Progress') {
            return 'Pending';
        }

        // For Not Represented cases, the action was to decline
        if (selectedTabFilter === 'not_disputed') {
            return 'Accept';
        }

        // Default fallback
        return verdict || 'Pending';
    };

    // Get the verdict value to display
    const verdictValue = getOutcomeText();

    return (
        <VerdictType
            verdict={verdictValue}
            size="small"
            className={'emp-verdictIcon'}
        />
    )
};

export const ActionsCell = ({
    rowData,
    rehydrateView,
    testId,
    selectedTabFilter,
    setAlertSnackbarMainProps,
    setAlertSnackbarMainOpen,
    showClientTypeIssuerUI,
    selectedTileType,
}: ActionsCellProps): React.ReactElement => {

    // If the selected tab filter is qc_review, show the ReviewCell
    if (selectedTabFilter === 'qc_review' && showClientTypeIssuerUI) {
        return (
            <ReviewCell
                caseId={rowData.data.id}
                setAlertProps={setAlertSnackbarMainProps}
                setAlertOpen={setAlertSnackbarMainOpen}
                rowIndex={rowData.rowIndex}
                rowData={rowData.data}
                filterTab={'review_pending'}
            />
        )
    }

    // Otherwise show the default DnrActionIcon
    return (
        <div className={`ccImage`} id={'caseMgmtAGActionsCell'}>
            <DnrActionIcon
                row={{ id: rowData.rowIndex, original: rowData.data }}
                rehydrateView={rehydrateView}
                testId={testId}
                selectedTabFilter={selectedTabFilter}
                setAlertSnackbarMainProps={setAlertSnackbarMainProps}
                setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                showClientTypeIssuerUI={showClientTypeIssuerUI}
                showDNRAction={
                    !showClientTypeIssuerUI ||
                    (showClientTypeIssuerUI &&
                        !['new_cases', 'assigned', 'expired'].includes(
                            selectedTabFilter
                        ))
                }
                selectedTileType={selectedTileType}
            />
        </div>
    )
}

export const DnrOnlyCell = ({
    rowData,
    rehydrateView,
    testId,
    selectedTabFilter,
    setAlertSnackbarMainProps,
    setAlertSnackbarMainOpen,
    showClientTypeIssuerUI,
}: ActionsCellProps): React.ReactElement => {

    // Otherwise show the default DnrActionIcon
    return (
        <div className={`ccImage`} id={'caseMgmtAGActionsCell'}>
            <DnrActionIcon
                row={{ id: rowData.rowIndex, original: rowData.data }}
                rehydrateView={rehydrateView}
                testId={testId}
                selectedTabFilter={selectedTabFilter}
                setAlertSnackbarMainProps={setAlertSnackbarMainProps}
                setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                showClientTypeIssuerUI={showClientTypeIssuerUI}
                showDNRActionOnly
            />
        </div>
    )
}

export const LastUserActionCell = ({ row }: { row: any }) => {
    return (
        <div className={'caseMgmtAGLastUserActionCell'}>
            <span className={`emp-lastUserAction`}>{row.last_user_action}</span>
        </div>
    )
}

export const DueDateCell = ({ row }: { row: any }) => {

    switch (row.case_note?.toLocaleLowerCase()) {
        case 'missing document':
            return <div id={'caseMgmtAGDueDateCell'}>
                <Tooltip arrow placement="top" title={'Not yet submitted, please check Case Note'}>
                    <IconButton className={`emp-noteIconBtn`} onClick={() => { }}>
                        <FontAwesomeIcon
                            icon={faStickyNote}
                            className={'emp-noteIcon'}
                        />
                    </IconButton>
                </Tooltip>
            </div>
        case 'save for later':
            return <div id={'caseMgmtAGDueDateCell'}> {row.due_date}
                <Tooltip arrow placement="top" title={'Case will be moved to Draft upon expiration'}>
                    <IconButton className={`emp-infoIconBtn`} onClick={() => { }}>
                        <FontAwesomeIcon
                            icon={faInfoCircle}
                            size="xs"
                            className={'emp-infoIcon'}
                        />
                    </IconButton>
                </Tooltip></div>
        default:
            return <div id={'caseMgmtAGDueDateCell'}> {row.due_date}</div>
    }
}
